import { useEffect, useState, type KeyboardEvent } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CurrencyInput } from "@/components/ui/currency-input";
import { z } from "zod";
import { AddressInput, cleanupPacContainers } from "@/components/form-fields/address-input";
import type { Facility } from "@shared/schema";

const facilityLinkSchema = z.object({
  facilityId: z.string(),
  facilityName: z.string().optional(),
  linked: z.boolean().default(true),
  isFirstRanking: z.boolean().default(true),
  securityPosition: z.enum(["first", "second", "third", "subordinated"]).default("first"),
  includeInLvr: z.boolean().default(true),
  lvrContributionPercentage: z.string().optional().nullable(),
  securedAmount: z.string().optional().nullable(),
});

const securityFormSchema = z.object({
  securityType: z.enum([
    "residential_property",
    "commercial_property",
    "vehicle",
    "equipment",
    "financial_asset",
    "guarantee",
    "general_security_agreement",
  ]),
  status: z.enum([
    "identified",
    "proposed",
    "valued",
    "approved",
    "registered",
    "varied",
    "substituted",
    "discharged",
    "cancelled",
  ]).default("identified"),
  estimatedValue: z.string().min(1, "Estimated value is required"),
  collateralType: z.enum([
    "registered_mortgage",
    "specific_security_agreement",
    "general_security_agreement",
    "ppsr_registration",
    "term_deposit",
    "caveat",
    "residual_stock",
  ]).optional().nullable(),
  valuationSource: z.enum([
    "avm_corelogic",
    "avm_proptrack",
    "avm_valocity",
    "full_valuation",
    "desktop_valuation",
    "kerbside_valuation",
    "contract_price",
    "bank_valuation",
    "self_assessed",
    "redbook",
    "glass_guide",
    "manual_entry",
  ]).optional().nullable(),
  description: z.string().optional().nullable(),
  isExistingSecurity: z.boolean().default(false),
  notes: z.string().optional().nullable(),
  vehicleType: z.enum([
    "motor_vehicle",
    "motorcycle",
    "caravan",
    "marine_boat",
    "marine_jetski",
    "trailer",
    "other_vehicle",
  ]).optional().nullable(),
  vehicleVin: z.string().optional().nullable(),
  vehicleRegistrationNumber: z.string().optional().nullable(),
  vehicleMake: z.string().optional().nullable(),
  vehicleModel: z.string().optional().nullable(),
  addressLine1: z.string().optional().nullable(),
  addressLine2: z.string().optional().nullable(),
  suburb: z.string().optional().nullable(),
  state: z.enum(["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"]).optional().nullable(),
  postcode: z.string().optional().nullable(),
  facilityLinks: z.array(facilityLinkSchema).default([]),
});

type SecurityFormData = z.infer<typeof securityFormSchema>;

interface SecurityFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (securityId: string) => void;
  mode?: "standalone" | "deal";
  dealId?: string;
}

const SECURITY_TYPE_OPTIONS = [
  { value: "residential_property", label: "Residential Property" },
  { value: "commercial_property", label: "Commercial Property" },
  { value: "vehicle", label: "Vehicle" },
  { value: "equipment", label: "Equipment" },
  { value: "financial_asset", label: "Financial Asset" },
  { value: "guarantee", label: "Guarantee" },
  { value: "general_security_agreement", label: "General Security Agreement" },
];

const STATUS_OPTIONS = [
  { value: "identified", label: "Identified" },
  { value: "proposed", label: "Proposed" },
  { value: "valued", label: "Valued" },
  { value: "approved", label: "Approved" },
  { value: "registered", label: "Registered" },
  { value: "varied", label: "Varied" },
  { value: "substituted", label: "Substituted" },
  { value: "discharged", label: "Discharged" },
  { value: "cancelled", label: "Cancelled" },
];

const COLLATERAL_TYPE_OPTIONS = [
  { value: "ppsr_registration", label: "PPSR Registration" },
  { value: "specific_security_agreement", label: "Specific Security Agreement" },
  { value: "general_security_agreement", label: "General Security Agreement" },
  { value: "registered_mortgage", label: "Registered Mortgage" },
  { value: "term_deposit", label: "Term Deposit" },
  { value: "caveat", label: "Caveat" },
  { value: "residual_stock", label: "Residual Stock" },
];

const VALUATION_SOURCE_OPTIONS = [
  { value: "contract_price", label: "Contract Price" },
  { value: "redbook", label: "RedBook" },
  { value: "glass_guide", label: "Glass's Guide" },
  { value: "manual_entry", label: "Manual Entry" },
  { value: "bank_valuation", label: "Bank Valuation" },
  { value: "self_assessed", label: "Self Assessed" },
];

const VEHICLE_TYPE_OPTIONS = [
  { value: "motor_vehicle", label: "Motor Vehicle" },
  { value: "motorcycle", label: "Motorcycle" },
  { value: "caravan", label: "Caravan" },
  { value: "marine_boat", label: "Marine Boat" },
  { value: "marine_jetski", label: "Marine Jetski" },
  { value: "trailer", label: "Trailer" },
  { value: "other_vehicle", label: "Other Vehicle" },
];

export function SecurityFormModal({
  open,
  onOpenChange,
  onSuccess,
  mode = "standalone",
  dealId,
}: SecurityFormModalProps) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [useManualEntry, setUseManualEntry] = useState(false);
  const isDealMode = mode === "deal";

  const form = useForm<SecurityFormData>({
    resolver: zodResolver(securityFormSchema),
    defaultValues: {
      securityType: "residential_property",
      status: "proposed",
      estimatedValue: "",
      collateralType: "ppsr_registration",
      valuationSource: "contract_price",
      description: "",
      isExistingSecurity: false,
      notes: "",
      vehicleType: "motor_vehicle",
      vehicleVin: "",
      vehicleRegistrationNumber: "",
      vehicleMake: "",
      vehicleModel: "",
      addressLine1: "",
      addressLine2: "",
      suburb: "",
      state: null,
      postcode: "",
      facilityLinks: [],
    },
  });

  const { fields: facilityLinkFields } = useFieldArray({
    control: form.control,
    name: "facilityLinks",
  });

  const { data: facilities = [] } = useQuery<Facility[]>({
    queryKey: [`/api/crm/deals/${dealId}/facilities`],
    enabled: open && isDealMode && Boolean(dealId),
  });

  const securityType = form.watch("securityType");
  const isPropertyType =
    securityType === "residential_property" || securityType === "commercial_property";

  useEffect(() => {
    if (!open) {
      setUseManualEntry(false);
      cleanupPacContainers();
    }
  }, [open]);

  useEffect(() => {
    if (!isDealMode) return;

    const currentLinks = form.getValues("facilityLinks");
    const currentById = new Map(currentLinks.map((link) => [link.facilityId, link]));

    form.setValue(
      "facilityLinks",
      facilities.map((facility) => {
        const existing = currentById.get(facility.id);
        return {
          facilityId: facility.id,
          facilityName: facility.facilityName || "Unnamed facility",
          linked: existing?.linked ?? false,
          isFirstRanking: existing?.isFirstRanking ?? true,
          securityPosition: existing?.securityPosition ?? "first",
          includeInLvr: existing?.includeInLvr ?? true,
          lvrContributionPercentage: existing?.lvrContributionPercentage ?? "100",
          securedAmount: existing?.securedAmount ?? "",
        };
      }),
      { shouldDirty: false },
    );
  }, [facilities, form, isDealMode]);

  const createMutation = useMutation({
    mutationFn: async (data: SecurityFormData) => {
      const value = parseFloat(data.estimatedValue);
      if (isNaN(value) || value < 0) {
        throw new Error("Estimated value must be a positive number");
      }

      const securityPayload = {
        securityType: data.securityType,
        collateralType: data.collateralType || null,
        status: data.status,
        estimatedValue: data.estimatedValue,
        valuationSource: data.valuationSource || "manual_entry",
        description: data.description || null,
        isExistingSecurity: data.isExistingSecurity,
        notes: data.notes || null,
      };

      const response = isDealMode
        ? await apiRequest("POST", `/api/crm/deals/${dealId}/securities/create`, {
            security: securityPayload,
            property: isPropertyType && data.addressLine1
              ? {
                  isResidential: data.securityType === "residential_property",
                  addressLine1: data.addressLine1,
                  addressLine2: data.addressLine2 || null,
                  suburb: data.suburb || null,
                  state: data.state || null,
                  postcode: data.postcode || null,
                }
              : undefined,
            facilityLinks: data.facilityLinks
              .filter((link) => link.linked)
              .map((link) => ({
                facilityId: link.facilityId,
                isFirstRanking: link.isFirstRanking,
                securityPosition: link.securityPosition,
                includeInLvr: link.includeInLvr,
                lvrContributionPercentage: link.lvrContributionPercentage || "100",
                securedAmount: link.securedAmount || null,
              })),
          })
        : await apiRequest("POST", "/api/crm/securities", {
            ...securityPayload,
            property: isPropertyType && data.addressLine1
              ? {
                  isResidential: data.securityType === "residential_property",
                  addressLine1: data.addressLine1,
                  addressLine2: data.addressLine2 || null,
                  suburb: data.suburb || null,
                  state: data.state || null,
                  postcode: data.postcode || null,
                }
              : undefined,
          });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(error || "Failed to create security");
      }

      const result = await response.json();
      const createdSecurity = isDealMode ? result.security : result;
      if (data.securityType === "vehicle" && createdSecurity?.id) {
        await apiRequest("POST", `/api/crm/securities/${createdSecurity.id}/vehicle`, {
          vehicleType: data.vehicleType || "motor_vehicle",
          vin: data.vehicleVin || null,
          registrationNumber: data.vehicleRegistrationNumber || null,
          make: data.vehicleMake || null,
          model: data.vehicleModel || null,
          validationSource: data.valuationSource || "contract_price",
        });
      }
      return createdSecurity;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/securities"] });
      if (isDealMode && dealId) {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0]?.toString().startsWith(`/api/crm/deals/${dealId}/securities`) ||
            false,
        });
      }

      toast({
        title: "Success",
        description: isDealMode
          ? "Security created in the deal register"
          : "Security created successfully",
      });

      onOpenChange(false);
      form.reset();

      if (!isDealMode && data?.id) {
        navigate(`/securities/${data.id}`);
      }
      if (data?.id) onSuccess?.(data.id);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: SecurityFormData) => {
    createMutation.mutate(data);
  };

  const handleFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      void form.handleSubmit(onSubmit)();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`${isDealMode ? "sm:max-w-[720px]" : "sm:max-w-[500px]"} max-h-[90vh] overflow-y-auto`}
        onInteractOutside={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest(".pac-container")) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Add Security</DialogTitle>
          <DialogDescription>
            {isDealMode
              ? "Create one canonical security record and link it to any facilities that need it."
              : "Create a new security record"}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            onKeyDown={handleFormKeyDown}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="securityType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Security Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {SECURITY_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {STATUS_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="estimatedValue"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estimated Value</FormLabel>
                  <FormControl>
                    <CurrencyInput
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="0.00"
                      data-testid="estimated-value-input"
                    />
                  </FormControl>
                  <FormDescription>Current estimated market value</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {securityType === "vehicle" && (
              <div className="border rounded-lg p-3 space-y-3">
                <h4 className="text-sm font-medium">Vehicle Details</h4>
                <div className="grid gap-3 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="collateralType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Collateral Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || "ppsr_registration"}>
                          <FormControl>
                            <SelectTrigger data-testid="select-vehicle-collateral-type">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {COLLATERAL_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="valuationSource"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Valuation Source</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || "contract_price"}>
                          <FormControl>
                            <SelectTrigger data-testid="select-vehicle-valuation-source">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {VALUATION_SOURCE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Vehicle Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value || "motor_vehicle"}>
                          <FormControl>
                            <SelectTrigger data-testid="select-vehicle-type">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {VEHICLE_TYPE_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleVin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>VIN</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-vehicle-vin"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleRegistrationNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Registration</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-vehicle-registration"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleMake"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Make</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-vehicle-make"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="vehicleModel"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Model</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value || ""}
                            data-testid="input-vehicle-model"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            )}

            {!isPropertyType && (
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description of the security..."
                        className="min-h-[80px]"
                        {...field}
                        value={field.value || ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {isPropertyType && (
              <div className="border rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Property Address</h4>
                  {!useManualEntry && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setUseManualEntry(true)}
                      className="text-xs"
                    >
                      Enter manually
                    </Button>
                  )}
                </div>

                {!useManualEntry ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium leading-none">
                        Search Address
                      </label>
                      <div className="mt-2">
                        <AddressInput
                          value=""
                          onAddressSelect={(address) => {
                            const unitAndStreet = [
                              address.unitNumber,
                              address.streetNumber,
                              address.streetName,
                            ]
                              .filter(Boolean)
                              .join(" ");

                            form.setValue("addressLine1", unitAndStreet || "");
                            form.setValue("suburb", address.suburb || "");
                            form.setValue("state", (address.state as SecurityFormData["state"]) || null);
                            form.setValue("postcode", address.postcode || "");
                          }}
                          placeholder="Search property address..."
                        />
                      </div>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Can&apos;t find the address? Use manual entry instead.
                    </div>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="addressLine1"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Address Line 1</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="addressLine2"
                      render={({ field }) => (
                        <FormItem className="md:col-span-2">
                          <FormLabel>Address Line 2</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="suburb"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Suburb</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>State</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value || undefined}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {["NSW", "VIC", "QLD", "SA", "WA", "TAS", "NT", "ACT"].map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="postcode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Postcode</FormLabel>
                          <FormControl>
                            <Input {...field} value={field.value || ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}
              </div>
            )}

            {isDealMode && (
              <div className="border rounded-lg p-4 space-y-3">
                <div>
                  <h4 className="text-sm font-medium">Linked Facilities</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    Add the security once, then choose which facilities it secures.
                  </p>
                  {facilityLinkFields.length === 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      No facilities exist yet. You can still save this as a canonical deal-level
                      security and link it later.
                    </p>
                  )}
                </div>

                {facilityLinkFields.length > 0 && (
                  <div className="space-y-3">
                    {facilityLinkFields.map((field, index) => (
                      <div key={field.id} className="space-y-2">
                        <div
                          data-testid={`facility-link-name-${index}`}
                          className="font-medium text-sm"
                        >
                          {field.facilityName || "Facility"}
                        </div>

                        <div
                          data-testid={`facility-link-row-${index}`}
                          className={`grid items-start gap-3 rounded-md border p-3 md:grid-cols-[120px_140px_140px_110px_minmax(0,1fr)] ${
                            form.watch(`facilityLinks.${index}.linked`)
                              ? "bg-background"
                              : "bg-muted/30 opacity-60"
                          }`}
                        >
                          <FormField
                            control={form.control}
                            name={`facilityLinks.${index}.linked`}
                            render={({ field }) => (
                              <FormItem className="space-y-2">
                                <FormLabel className="text-xs">Linked?</FormLabel>
                                <FormControl>
                                  <label className="flex min-h-10 items-center gap-2 rounded-md border px-3 py-2">
                                    <Checkbox
                                      checked={field.value}
                                      onCheckedChange={(checked) => field.onChange(Boolean(checked))}
                                      data-testid={`facility-linked-checkbox-${index}`}
                                    />
                                    <span className="text-sm">Link</span>
                                  </label>
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`facilityLinks.${index}.securityPosition`}
                            render={({ field }) => {
                              const isLinked = form.watch(`facilityLinks.${index}.linked`);
                              return (
                                <FormItem>
                                  <FormLabel className="text-xs">Ranking</FormLabel>
                                  <Select
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      form.setValue(
                                        `facilityLinks.${index}.isFirstRanking`,
                                        value === "first",
                                      );
                                    }}
                                    value={field.value}
                                    disabled={!isLinked}
                                  >
                                    <FormControl>
                                      <SelectTrigger>
                                        <SelectValue />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      <SelectItem value="first">First</SelectItem>
                                      <SelectItem value="second">Second</SelectItem>
                                      <SelectItem value="third">Third</SelectItem>
                                      <SelectItem value="subordinated">Subordinated</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </FormItem>
                              );
                            }}
                          />

                          <FormField
                            control={form.control}
                            name={`facilityLinks.${index}.includeInLvr`}
                            render={({ field }) => {
                              const isLinked = form.watch(`facilityLinks.${index}.linked`);
                              return (
                                <FormItem className="space-y-2">
                                  <FormLabel className="text-xs">Include in LVR</FormLabel>
                                  <FormControl>
                                    <div className="flex min-h-10 items-center justify-between rounded-md border px-3 py-2">
                                      <span className="text-sm text-muted-foreground">Include</span>
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                        disabled={!isLinked}
                                        data-testid={`facility-lvr-switch-${index}`}
                                      />
                                    </div>
                                  </FormControl>
                                </FormItem>
                              );
                            }}
                          />

                          <FormField
                            control={form.control}
                            name={`facilityLinks.${index}.lvrContributionPercentage`}
                            render={({ field }) => {
                              const isLinked = form.watch(`facilityLinks.${index}.linked`);
                              return (
                                <FormItem>
                                  <FormLabel className="text-xs">LVR %</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      value={field.value || ""}
                                      placeholder="100"
                                      disabled={!isLinked}
                                    />
                                  </FormControl>
                                </FormItem>
                              );
                            }}
                          />

                          <FormField
                            control={form.control}
                            name={`facilityLinks.${index}.securedAmount`}
                            render={({ field }) => {
                              const isLinked = form.watch(`facilityLinks.${index}.linked`);
                              return (
                                <FormItem>
                                  <FormLabel className="text-xs">Secured Amount</FormLabel>
                                  <FormControl>
                                    <CurrencyInput
                                      value={field.value || ""}
                                      onValueChange={field.onChange}
                                      placeholder="0.00"
                                      disabled={!isLinked}
                                    />
                                  </FormControl>
                                </FormItem>
                              );
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isDealMode ? "Save Security" : "Create Security"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
